import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Chip,
  Box,
  Typography,
  IconButton,
  Grid,
  Divider,
  Autocomplete
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import useReportConfigs from 'hooks/results/use-report-configs';
import useGenerateReport from 'hooks/results/use-generate-report';

const GenerateReportDialog = ({ open, onClose, onGenerate, experimentId }) => {
  const [description, setDescription] = useState('');
  const [navigateToPipeline, setNavigateToPipeline] = useState(true);
  const [selectedVerticals, setSelectedVerticals] = useState([]);
  const [allVerticalOptions, setAllVerticalOptions] = useState([]);
  const navigate = useNavigate();
  const { data: reportConfig } = useReportConfigs({ experimentId });
  const { mutate: triggerGenerateReport, isLoading: isGenerating } = useGenerateReport();


  useEffect(() => {
    if (open && reportConfig) {
      const all = reportConfig.payload?.verticals?.all_verticals || [];
      const selected = reportConfig.payload?.verticals?.selected_verticals || [];

      setAllVerticalOptions(all);
      setSelectedVerticals(selected); // { id, name }
      setDescription(reportConfig.payload?.program_description || '');
    }
  }, [open, reportConfig]);

  const handleGenerate = () => {
    const verticalIds = selectedVerticals.map((v) => v.id);

    const payload = {
      experiment_id: experimentId,
      verticals: verticalIds,
      program_description: description,
      generate_report: true
    };

    triggerGenerateReport(payload, {
      onSuccess: (res) => {
        onClose();
        if (navigateToPipeline) {
          navigate('/configure/pipelines');
        } else {
          navigate('/evaluate/results');
        }
      },
      onError: (err) => {
        console.error('Failed to trigger report generation:', err);
      }
    });
  };


  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <Box sx={{ mt: 5, mx: 5, mb: 2 }}>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 30,
            right: 30,
            backgroundColor: '#333333',
            padding: '4px',
            zIndex: 1,
            '&:hover': { backgroundColor: '#444444' },
          }}
        >
          <CloseIcon sx={{ color: '#ffffff', fontSize: '1.2rem' }} />
        </IconButton>

        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.5rem', pl: 0 }}>
          Generate Report
        </DialogTitle>
        <Divider sx={{ borderBottom: '2px solid black', mb: 3 }} />

        <DialogContent sx={{ px: 0 }}>
          <Grid container spacing={2} alignItems="flex-start">
            {/* Verticals */}
            <Grid item xs={3}>
              <Typography variant="subtitle1">Verticals</Typography>
            </Grid>
            <Grid item xs={9}>
              <Autocomplete
                multiple
                id="verticals"
                fullWidth
                freeSolo
                options={allVerticalOptions}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                value={selectedVerticals}
                filterSelectedOptions
                onChange={(event, value) => {
                  const unique = [];
                  const seenIds = new Set();

                  value.forEach((v) => {
                    if (typeof v === 'string') {
                      const existing = allVerticalOptions.find(opt => opt.name === v);
                      if (existing && !seenIds.has(existing.id)) {
                        seenIds.add(existing.id);
                        unique.push(existing);
                      }
                    } else if (!seenIds.has(v.id)) {
                      seenIds.add(v.id);
                      unique.push(v);
                    }
                  });

                  setSelectedVerticals(unique);
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip key={option.id || option.name} label={option.name} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Add  verticals"
                    variant="outlined"
                    size="small"
                    helperText="Eg. LLM agents in education"
                  />
                )}
              />
            </Grid>

            {/* Program Description */}
            <Grid item xs={3} sx={{ mt: 3 }}>
              <Typography variant="subtitle1">Program Description</Typography>
            </Grid>
            <Grid item xs={9} sx={{ mt: 3 }}>
              <TextField
                multiline
                rows={6}
                fullWidth
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                variant="outlined"
              />
            </Grid>

            {/* Navigate Checkbox */}
            <Grid item xs={3} />
            <Grid item xs={9}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={navigateToPipeline}
                    onChange={(e) => setNavigateToPipeline(e.target.checked)}
                  />
                }
                label="Check Report Progress"
                sx={{ mt: 2 }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ pr: 0, pt: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleGenerate} variant="contained" color="primary" disabled={isGenerating}>
            {isGenerating ? 'Generating...' : 'Generate'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default GenerateReportDialog;
